import { useEffect, useEffectEvent, useMemo, useRef, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import { cn } from '@/lib/utils';
import { acquireOverlay, isOverlayTopmost, releaseOverlay } from './overlayStack';

export interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Element that toggles the popover (excluded from outside-click). */
  triggerRef?: RefObject<HTMLElement | null>;
  /** Optional external ref for the popover content element. */
  contentRef?: RefObject<HTMLDivElement | null>;
  /** Whether to render a fullscreen backdrop behind the popover. */
  modal?: boolean;
  /** Backdrop aria-label (when modal=true). */
  backdropAriaLabel?: string;
  /** Close on Escape key. */
  closeOnEscape?: boolean;
  /** Render in a portal (recommended for fixed overlays inside modals). */
  portal?: boolean;
  /** Class for the popover content wrapper. */
  contentClassName?: string;
  /** Inline style for the popover content wrapper. */
  contentStyle?: CSSProperties;
  /** z-index for the content (backdrop uses contentZIndex-10 by default). */
  contentZIndex?: number;
  children: ReactNode;
}

export function Popover({
  open,
  onOpenChange,
  triggerRef,
  contentRef,
  modal = true,
  backdropAriaLabel,
  closeOnEscape = true,
  portal = true,
  contentClassName,
  contentStyle,
  contentZIndex,
  children,
}: PopoverProps) {
  const internalContentRef = useRef<HTMLDivElement>(null);
  const resolvedContentRef = contentRef ?? internalContentRef;
  const overlayTokenRef = useRef<symbol | null>(null);

  const refsForOutside = useMemo(() => {
    return triggerRef ? [triggerRef, resolvedContentRef] : [resolvedContentRef];
  }, [resolvedContentRef, triggerRef]);

  const closePopover = useEffectEvent(() => {
    onOpenChange(false);
  });

  useOnClickOutside(refsForOutside, () => onOpenChange(false), { enabled: open });

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && overlayTokenRef.current && isOverlayTopmost(overlayTokenRef.current)) {
        closePopover();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeOnEscape]);

  useEffect(() => {
    if (!open) return;
    const token = acquireOverlay();
    overlayTokenRef.current = token;
    return () => {
      releaseOverlay(token);
      if (overlayTokenRef.current === token) overlayTokenRef.current = null;
    };
  }, [open]);

  if (!open) return null;

  const body = (
    <>
      {modal && (
        <button
          type='button'
          className={cn('fixed inset-0 cursor-default bg-transparent', contentZIndex ? undefined : 'z-40')}
          style={contentZIndex ? { zIndex: contentZIndex - 10 } : undefined}
          aria-label={backdropAriaLabel ?? 'Close'}
          onClick={() => onOpenChange(false)}
        />
      )}
      <div
        ref={resolvedContentRef}
        className={cn(contentZIndex ? undefined : 'z-50', contentClassName)}
        style={{ ...(contentZIndex ? { zIndex: contentZIndex } : null), ...contentStyle }}
      >
        {children}
      </div>
    </>
  );

  return portal ? createPortal(body, document.body) : body;
}
